import { updateObjectValues } from '@/utils/helpers';
import api from '@/services/api';
import ls from '@/helpers/localStorage';
import { ACCOUNT_FUNDS_MAP } from '@/services/notifications/proxy';

const BALANCE_DEFAULT = {
  accountBalance: '$0.00',
  accountBalanceDecimal: 0,
  portfolioBalance: '$0.00',
  portfolioBalanceDecimal: 0,
  shareValueDelta: '$0.00',
  shareValueDeltaDecimal: 0,
  isDefault: true,
};

const DEFAULT_WIRE_SEARCH = {
  data: [],
  page: 0,
  totalCount: 0,
  canLoadMore: true,
};

export default {
  namespaced: true,
  state: {
    ach: {},
    check: {},
    wire: {},
    withdrawCoinbase: {},
    depositMethods: {},
    balance: BALANCE_DEFAULT,
    wireSearchResults: Object.create(DEFAULT_WIRE_SEARCH),
    pendingRequests: [],
    pendingDepositsRequests: [],
    currentDeposit: {},
    firstDepositDone: ls.get('firstDepositDone', false),
    bankTransferStep: {},
  },
  getters: {
    portfolioValueDecimal: (state, getters, rotState, rootGetters) => {
      const totalValue = state.balance.accountBalanceDecimal +
        state.balance.portfolioBalanceDecimal +
        rootGetters['dashboard/mySharesTotalGainLoss'];
      return Math.floor(totalValue * 100) / 100;
    },
    portfolioValue: (state, getters) => {
      const value = getters.portfolioValueDecimal.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      return `$${value}`;
    },
  },
  mutations: {
    setBalance(state, payload) {
      state.balance = payload;
    },
    setCurrentDeposit(state, payload) {
      state.currentDeposit = payload;
    },
    setWire(state, data) {
      state.wire = data;
    },
    setAch(state, data) {
      state.ach = data;
    },
    setCheck(state, data) {
      state.check = data;
    },
    setWithdrawCoinbase(state, data) {
      state.withdrawCoinbase = data;
    },
    setPendingRequests(state, data) {
      state.pendingRequests = data;
    },
    setPendingDepositsRequests(state, data) {
      state.pendingDepositsRequests = data;
    },
    updateBalanceFromNotification(state, data) {
      state.balance = updateObjectValues(state.balance, data, ACCOUNT_FUNDS_MAP);
    },
    markFirstDepositDone(state) {
      const expiresAt = 31536000; // 1 year
      ls.set('firstDepositDone', true, expiresAt);
      state.firstDepositDone = true;
    },
    setDepositMethods(state, value) {
      state.depositMethods = value;
    },
    resetWireSearchResults(state) {
      state.wireSearchResults = Object.create(DEFAULT_WIRE_SEARCH);
    },
    setWireSearchResults(state, payload) {
      const sourceData = [...state.wireSearchResults.data];
      const data = sourceData.concat(payload.data.results || []);
      const page = payload.data.page;
      const totalCount = payload.data.totalResults;
      const canLoadMore = totalCount > data.length;

      state.wireSearchResults = {
        ...state.wireSearchResults,
        data,
        page,
        totalCount,
        canLoadMore,
      };
    },
    setBankTransferStep(state, data) {
      state.bankTransferStep = data;
    },
  },
  actions: {
    clearBalance({ commit }) {
      commit('setBalance', BALANCE_DEFAULT);
    },
    getBalance({ commit }) {
      return api.wallet.getBalance()
        .then((response) => {
          commit('setBalance', response.data);
          return response.data;
        });
    },
    getDepositCard() {
      return api.wallet.getDepositCard()
        .then(response => response.data);
    },
    confirmDepositCard({ commit }, payload) {
      return api.wallet.postConfirmDepositCard(payload)
        .then(response => response.data);
    },
    submitDepositCard({ commit }, payload) {
      return api.wallet.postSubmitDepositCard(payload)
        .then(response => response.data);
    },
    submitDepositUSDCoin({ commit }, payload) {
      return api.wallet.postSubmitDepositUSDCoin(payload)
        .then(response => response.data);
    },
    setCurrentDeposit({ commit }, value) {
      commit('setCurrentDeposit', value);
    },
    getPayNearMe({ commit }, page) {
      return api.wallet.getPayNearMe(page)
        .then(response => response.data);
    },
    getDepositUSDCoin() {
      return api.wallet.getDepositUSDCoin()
        .then(response => response.data);
    },
    resetWireSearchResults({ commit }) {
      commit('resetWireSearchResults');
    },
    getWithdrawWireBankSearch({ state, commit }, { query = '' }) {
      if (state.wireSearchResults.canLoadMore) {
        const page = state.wireSearchResults.page + 1;
        return api.wallet.getWithdrawWireBankSearch(page, query)
          .then((response) => {
            commit('setWireSearchResults', {
              page,
              data: response.data,
            });
            return response.data;
          });
      }
      return null;
    },
    getWithdrawWire({ commit }) {
      return api.wallet.getWithdrawWire()
        .then((response) => {
          commit('setWire', response.data);
          return response.data;
        });
    },
    getWithdrawAch({ commit }) {
      return api.wallet.getWithdrawAch()
        .then((response) => {
          commit('setAch', response.data);
          return response.data;
        });
    },
    getWithdrawCheck({ commit }) {
      return api.wallet.getWithdrawCheck()
        .then((response) => {
          commit('setCheck', response.data);
          return response.data;
        });
    },
    getWithdrawCoinbase({ commit }) {
      return api.wallet.getWithdrawUSDC()
        .then((response) => {
          commit('setWithdrawCoinbase', response.data);
          return response.data;
        });
    },
    postWithdrawAch({ dispatch }, payload) {
      return api.wallet.postWithdrawAch(payload)
        .then((response) => {
          dispatch('getBalance');
          return response.data;
        });
    },
    postWithdrawCheck({ dispatch }, payload) {
      return api.wallet.postWithdrawCheck(payload)
        .then((response) => {
          dispatch('getBalance');
          return response.data;
        });
    },
    postWithdrawCoinbase({ dispatch }, payload) {
      return api.wallet.postWithdrawUSDC(payload)
        .then((response) => {
          dispatch('getBalance');
          return response.data;
        });
    },
    postWithdrawWireBankInfo({ dispatch }, payload) {
      return api.wallet.postWithdrawWireBankInfo(payload)
        .then(response => response.data);
    },
    postWithdrawWireConfirm(_, payload) {
      return api.wallet.postWithdrawWireConfirm(payload)
        .then(response => response.data);
    },
    postWithdrawWireSubmit(_, payload) {
      return api.wallet.postWithdrawWireSubmit(payload)
        .then(response => response.data);
    },
    getPendingWithdrawals({ commit }) {
      return api.wallet.getPendingWithdrawals()
        .then((response) => {
          commit('setPendingRequests', response.data);
          return response.data;
        });
    },
    getPendingDeposits({ commit }) {
      return api.wallet.getPendingDeposits()
        .then((response) => {
          commit('setPendingDepositsRequests', response.data);
          return response.data;
        });
    },
    cancelWithdrawal({ dispatch }, payload) {
      return api.wallet.postCancelWithdrawal(payload)
        .then((response) => {
          dispatch('getBalance');
          return response.data;
        });
    },
    updateBalanceFromNotification({ commit }, payload) {
      commit('updateBalanceFromNotification', payload);
    },
    getDepositMethods({ commit }) {
      return api.wallet.getDepositMethods()
        .then((response) => {
          const data = response.data;
          commit('setDepositMethods', data);
          return data;
        });
    },
    getWithdrawalMethods() {
      return api.wallet.getWithdrawalMethods()
        .then(response => response.data);
    },
    markFirstDepositDone({ commit }) {
      commit('markFirstDepositDone');
    },
    getDepositPWMB(_, { authorizationId = '', returnUrl = '', cancelUrl = '' } = {}) {
      return api.wallet.getDepositPWMB({ authorizationId, returnUrl, cancelUrl })
        .then(response => response.data);
    },
    deletePWMBAccount(_, authorizationId) {
      return api.wallet.deletePWMBAuthorization(authorizationId)
        .then(response => response.data);
    },
    confirmDepositPWMB(_, payload) {
      return api.wallet.postConfirmDepositPWMB(payload)
        .then(response => response.data);
    },
    submitDepositPWMB(_, payload) {
      return api.wallet.postSubmitDepositPWMB(payload)
        .then(response => response.data);
    },
    getPWMBAuthorization() {
      return api.wallet.getPWMBAuthorization()
        .then(response => response.data);
    },
    setBankTransferStep({ commit }, data) {
      commit('setBankTransferStep', data);
    },
    submitInstantCredit(_, promoCodeText) {
      return api.wallet.postInstantCredit({ promoCodeText })
        .then(response => response.data);
    },
  },
};



// WEBPACK FOOTER //
// ./src/store/modules/wallet.js